/**
 * Text  component
 *
 * @package ext-components-base
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React from "react";

import {
	Type,
	defineElementaryComponent,
	createSubScope,
	BP
} from "@adapptio/lib-blueprint";

import {
	TViewComponentDefinition,
	TComponentReact,
	Text,
	TextProps,
	getValuesFromStringEnum,
	termsEditor as LibComponentsTerms,
	VERTICAL_ALIGN,
	VERTICAL_ALIGN_default,
	ClassList,
	TextValueSchema,
	getStringEnumValue,
	propGroups,
	VERTICAL_ALIGN_opts,
	isEventEnabled,
	TTextPropsModelNode
} from "@adapptio/lib-components";
import { BaseEvents } from "../../base/BaseEvents";
import { termsEditor } from "../../terms";

interface ComponentText_State {}

const ComponentText_Props = {
	value: BP.Prop(
		TextValueSchema({
			alias: "textValue_text",
			default: "Text"
		}),
		TextProps.value.order,
		propGroups.common
	),

	align: BP.Prop(TextProps.align.schema, TextProps.align.order, propGroups.common),

	style: BP.Prop(TextProps.style.schema, TextProps.style.order, propGroups.common),

	fontSize: BP.Prop(TextProps.fontSize.schema, TextProps.fontSize.order, propGroups.common),

	foregroundColor: BP.Prop(
		TextProps.foregroundColor.schema,
		TextProps.foregroundColor.order,
		propGroups.common
	),

	overflow: BP.Prop(TextProps.overflow.schema, TextProps.overflow.order, propGroups.common),

	markdown: BP.Prop(TextProps.markdown.schema, TextProps.markdown.order, propGroups.common),

	html: BP.Prop(TextProps.html.schema, TextProps.html.order, propGroups.common),

	verticalAlign: BP.Prop(
		BP.Enum.String({
			...LibComponentsTerms.schemas.common.verticalAlign,
			options: getValuesFromStringEnum(
				VERTICAL_ALIGN,
				LibComponentsTerms.schemas.common.verticalAlignValues,
				VERTICAL_ALIGN_opts
			),
			default: VERTICAL_ALIGN_default,
			editorOptions: {
				controlType: "buttons"
			}
		}),
		100,
		propGroups.common
	)
};

const ComponentText_Events = {
	...BaseEvents
};

const ComponentText_Definition = defineElementaryComponent<
	typeof ComponentText_Props,
	ComponentText_State,
	typeof ComponentText_Events
>({
	...termsEditor.components.text.component,

	name: "text",

	category: "content",

	icon: "mdi/text",

	docUrl: "...",

	order: 10,

	props: ComponentText_Props,

	events: ComponentText_Events,

	initialWidth: 400,

	initialHeight: 36,

	resolve: (spec, state) => {
		return state || {};
	},

	getScopeData: (spec, state) => {
		return {};
	},

	getScopeType: () => {
		return Type.Object({
			props: {}
		});
	}
});

const ComponentText_React: TComponentReact<typeof ComponentText_Definition> = ({
	props,
	componentInstance,
	reactComponentClassList
}) => {
	const _clickHandler = React.useMemo(() => {
		if (isEventEnabled(componentInstance.eventEnabled.click, componentInstance.componentMode)) {
			return () => componentInstance.eventTriggers.click((parentScope) => createSubScope(parentScope));
		}

		return;
	}, [
		componentInstance.componentMode,
		componentInstance.eventEnabled.click,
		componentInstance.eventTriggers.click
	]);

	const classList = new ClassList();

	if (reactComponentClassList) {
		classList.add(...reactComponentClassList);
	}

	const verticalAlignValue = getStringEnumValue(
		VERTICAL_ALIGN,
		props.verticalAlign,
		VERTICAL_ALIGN_default
	);

	classList.add(`text--vertical-align-${verticalAlignValue}`);

	return (
		<Text
			{...props}
			componentPath={componentInstance.safePath}
			componentMode={componentInstance.componentMode}
			classList={classList}
			forceRender={true}
			propsModelNode={componentInstance.modelNode?.props as TTextPropsModelNode}
			onClick={_clickHandler}
		/>
	);
};

export const ComponentText: TViewComponentDefinition<typeof ComponentText_Definition> = {
	...ComponentText_Definition,
	reactComponent: ComponentText_React
};
